import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Icon,
  Card,
  VStack,
  HStack,
  Grid,
  GridItem,
  Image,
  Spinner,
} from '@chakra-ui/react';
import { useGetSalesSummary } from '@/services/dashboard';
import ChartIcon from '@/assets/png/chart.png';
import BoxIcon from '@/assets/png/box.png';
import DollarIcon from '@/assets/png/dollar-circle.png';
import ProfileIcon from '@/assets/png/profile-add.png';
import ArrowUpIcon from '@/assets/svg/arrow-up.svg?react';
import ArrowDownIcon from '@/assets/svg/arrow-down.svg?react';
import CustomSelect from '@/components/common/CustomSelect';
import { getCurrencySymbol } from '@/utils/currencyUtils';

interface Metric {
  current?: number | string;
  change_percentage?: number | string;
  trend?: 'up' | 'down' | string;
}

interface SalesSummaryData {
  metrics?: {
    sales?: Metric;
    orders?: Metric;
    quantity?: Metric;
    customers?: Metric;
  };
  currency?: string;
}

interface SummaryCardProps {
  label: string;
  value: string;
  percentage: string;
  trend: string;
  icon: string; // Changed from `any` to `string`
}

const SummaryCard: React.FC<SummaryCardProps> = ({ label, value, percentage, trend, icon }) => (
  <Box
    fontFamily={'primary'}
    p={3}
    borderWidth="1px"
    borderRadius="lg"
    width={{ base: '100%', md: '100%' }}
    bg={'background.secondary'}
  >
    <Flex justify="space-between" align="center">
      <VStack spacing={1} align={'start'}>
        <Text fontSize={'sm'} color={'text.secondary'}>
          {label}
        </Text>
        <HStack spacing={0}>
          <Text mr={2} fontSize={'2xl'} fontWeight={'semibold'}>
            {value}
          </Text>
          {trend === 'up' ? (
            <Icon boxSize={6} as={ArrowUpIcon} />
          ) : trend === 'down' ? (
            <Icon boxSize={6} as={ArrowDownIcon} />
          ) : (
            ''
          )}
          <Text
            mt={1}
            color={
              trend === 'up'
                ? 'status.success'
                : trend === 'down'
                  ? 'status.error'
                  : 'text.secondary'
            }
            fontSize={'xs'}
          >
            {percentage}%
          </Text>
        </HStack>
      </VStack>
      <Image boxSize={8} src={icon} />
    </Flex>
  </Box>
);

const timeframeOptions = [
  { id: 'weekly', label: 'This Week' },
  { id: 'daily', label: 'Today' },
  { id: 'monthly', label: 'This Month' },
  { id: 'yearly', label: 'This Year' },
];

const SalesSummary: React.FC = () => {
  const [timeframe, setTimeframe] = useState('weekly');
  const { data, isLoading } = useGetSalesSummary(timeframe) as {
    data: SalesSummaryData | undefined;
    isLoading: boolean;
  };

  return (
    <Card borderColor={'border.default60'} p={4} shadow={0} borderWidth={'1px'}>
      <Flex justify={'space-between'} align={'center'} mb={1}>
        <Text fontSize="xl" fontWeight="semibold" mb={4}>
          Sales Summary
        </Text>
        <Box>
          <CustomSelect
            options={timeframeOptions}
            selectedValue={timeframe}
            onChange={setTimeframe}
            placeholder="Select timeframe"
          />
        </Box>
      </Flex>

      {isLoading ? (
        <Flex justify="center" align="center" height="90px">
          <Spinner />
        </Flex>
      ) : (
        <Grid templateColumns={{ base: '1fr', md: 'repeat(4,1fr)' }} gap={5}>
          <GridItem>
            <SummaryCard
              label="Total Sale"
              value={`${getCurrencySymbol(data?.currency)}${data?.metrics?.sales?.current ?? '-'}`}
              percentage={`${data?.metrics?.sales?.change_percentage ?? '-'}`}
              trend={data?.metrics?.sales?.trend ?? ''}
              icon={ChartIcon}
            />
          </GridItem>
          <GridItem>
            <SummaryCard
              label="Total Orders"
              value={`${data?.metrics?.orders?.current ?? '-'}`}
              percentage={`${data?.metrics?.orders?.change_percentage ?? '-'}`}
              trend={data?.metrics?.orders?.trend ?? ''}
              icon={BoxIcon}
            />
          </GridItem>
          <GridItem>
            <SummaryCard
              label="Total Quantity"
              value={`${data?.metrics?.quantity?.current ?? '-'}`}
              percentage={`${data?.metrics?.quantity?.change_percentage ?? '-'}`}
              trend={data?.metrics?.quantity?.trend ?? ''}
              icon={DollarIcon}
            />
          </GridItem>
          <GridItem>
            <SummaryCard
              label="New Customers"
              value={`${data?.metrics?.customers?.current ?? '-'}`}
              percentage={`${data?.metrics?.customers?.change_percentage ?? '-'}`}
              trend={data?.metrics?.customers?.trend ?? ''}
              icon={ProfileIcon}
            />
          </GridItem>
        </Grid>
      )}
    </Card>
  );
};

export default SalesSummary;
